import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@weave-mui/material';
import Drawer from '@mui/material/Drawer';
import { Skeleton, List, ListItem, ListItemText, Divider } from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  PlaybackPlayM as PlaybackPlayIcon,
  TrashM as TrashIcon,
  EditM as EditIcon,
} from '@weave-mui/icons-weave';
import ScheduleModal from 'containers/TestSuites/ScheduleModal';
import ConfirmationModal from 'components/ConfirmationModal';
import ScheduleDisplay from 'components/ScheduleDisplay';
import {
  getTestSuiteDetails,
  removeTestSuite,
} from 'store/actions/test_suites_actions';

/**
 * TestSuiteDetailsDrawer - A drawer component that displays test suite details and actions
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Function to call when closing the drawer
 * @param {Function|null} [props.onShare=null] - Function to call when sharing the test suite
 * @param {Object|null} [props.testSuite=null] - The test suite object to display
 * @param {string} props.testSuite.testsuiteid - Test suite ID
 * @param {string} props.testSuite.testsuitename - Test suite name
 * @param {string} [props.testSuite.servicename] - Service name
 * @param {string} [props.testSuite.subcategory] - Subcategory
 * @param {string} [props.testSuite.emailrecipients] - Email recipients
 * @param {Object} [props.testSuite.userlist] - Shared users list
 * @returns {JSX.Element} The rendered drawer component
 */
const TestSuiteDetailsDrawer = ({
  open,
  onClose,
  onShare = null,
  onRun = null,
  testSuite = null,
}) => {
  // Get data from Redux state instead of props
  const userInfo = useSelector((state) => state.app.userInfo);
  const dispatch = useDispatch();
  const [copied, setCopied] = React.useState(false);
  const [openScheduleModal, setOpenScheduleModal] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [suiteDetails, setSuiteDetails] = React.useState(null);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleCopyId = () => {
    navigator.clipboard.writeText(testSuite.testsuiteid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditSchedule = () => {
    setOpenScheduleModal(true);
  };

  const handleShare = () => {
    if (onShare && testSuite) {
      onShare(testSuite);
    }
  };

  const handleEdit = () => {
    window.location.href = `/testsuites/${testSuite.testsuiteid}`;
  };

  const handleRun = () => {
    if (onRun && testSuite) {
      onRun(testSuite);
    }
  };

  const handleDelete = () => {
    setOpenDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    dispatch(removeTestSuite(testSuite.testsuiteid)).then(() => {
      setIsDeleting(false);
      setOpenDeleteModal(false);
      onClose(); // Close the drawer after deletion
    });
  };

  // Helper function to extract name from email
  const extractNameFromEmail = (email) => {
    if (!email || !email.includes('@')) return { firstName: '', lastName: '' };
    const namePart = email.split('@')[0];
    const nameParts = namePart.split('.');
    const firstName = nameParts[0]
      ? nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1)
      : '';
    const lastName = nameParts[1]
      ? nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1)
      : '';
    return { firstName, lastName };
  };

  // Fetch suite details when drawer opens (always from server; cache disabled for getTestSuite)
  React.useEffect(() => {
    if (open && testSuite && testSuite.testsuiteid) {
      setSuiteDetails(null); // Avoid showing previous suite data while loading
      setLoadingDetails(true);
      getTestSuiteDetails(testSuite.testsuiteid)
        .then((response) => {
          setSuiteDetails(response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch test suite details:', error);
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  }, [open, testSuite]);

  // Common accordion styling
  const accordionStyles = {
    mb: 3, // 24px spacing between sections
    backgroundColor: 'white !important',
    '& .MuiAccordionSummary-root': {
      border: '1px solid #CCC',
      padding: '8px 16px !important',
      backgroundColor: 'white !important',
    },
    '& .MuiAccordionDetails-root': {
      border: '1px solid #CCC',
      borderTop: 'none',
    },
    '&.MuiAccordionDetails-root': {
      paddingTop: '16px !important',
    },
    '&.MuiAccordion-root ': {
      marginBottom: '24px !important',
    },
  };

  // Common accordion summary styling
  const accordionSummaryStyles = {
    '& .MuiAccordionSummary-content': {
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '12px 0',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginLeft: 'auto',
      transition: 'transform 0.3s ease',
    },
    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(180deg) !important',
    },
  };

  if (!testSuite) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 700,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Close button row */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pb: 0 }}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Header */}
        <Box
          sx={{
            p: 3,
            pt: 1, // Reduced top padding since close button is above
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Left side - Suite name */}
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {testSuite.testsuitename}
          </Typography>

          {/* Right side - Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" color="primary" onClick={handleEdit}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={handleDelete}>
              <TrashIcon />
            </IconButton>
            <IconButton size="small" color="success" onClick={handleRun}>
              <PlaybackPlayIcon />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleRun}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                px: 2,
                ml: 1,
                height: '32px',
              }}
            >
              Run Suite
            </Button>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {/* Info Section */}
          <Accordion defaultExpanded position="right" sx={accordionStyles}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="info-content"
              id="info-header"
              sx={accordionSummaryStyles}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold !important', fontSize: '18px' }}
              >
                Info
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ overflow: 'hidden' }}>
              <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Created by
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                      }}
                    >
                      {testSuite.createdby || 'Unknown'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Category
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                      }}
                    >
                      {testSuite.category}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Last run
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                      }}
                    >
                      {testSuite.lastRun || 'Never'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Test suite ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          flex: 1,
                          minWidth: 0,
                        }}
                        title={testSuite.testsuiteid}
                      >
                        {testSuite.testsuiteid}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleCopyId}
                        sx={{
                          padding: '0',
                          minWidth: 'auto',
                          minHeight: '20px',
                          '& .MuiIconButtonWeave-iconContainer': {
                            minHeight: '20px !important ',
                            height: '20px !important',
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        title={copied ? 'Copied!' : 'Copy test suite ID'}
                      >
                        <CopyIcon
                          sx={{
                            fontSize: '16px',
                            color: copied ? 'success.main' : 'text.secondary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Environment
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                      }}
                    >
                      {testSuite.environment}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Modified by
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                      }}
                    >
                      {testSuite.modifiedby || 'Unknown'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Schedule Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold !important', fontSize: '18px', mb: 2 }}
            >
              Schedule
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {/* Left side - Schedule display */}
              <ScheduleDisplay
                scheduledtime={testSuite.scheduledtime}
                start_date={testSuite.start_date}
                end_date={testSuite.end_date}
                showTime={true}
                orientation="horizontal"
                size="medium"
              />

              {/* Right side - Edit schedule button */}
              <Button
                variant="text"
                size="small"
                onClick={handleEditSchedule}
                sx={{
                  textDecoration: 'underline',
                  color: 'text.primary',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'text.secondary',
                    textDecoration: 'underline',
                  },
                }}
              >
                Edit schedule
              </Button>
            </Box>
          </Box>
          {/* Tests Section */}
          <Accordion defaultExpanded position="right" sx={accordionStyles}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="tests-content"
              id="tests-header"
              sx={accordionSummaryStyles}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mr: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold !important', fontSize: '18px' }}
                >
                  Tests
                </Typography>
                {testSuite && (
                  <Button
                    component="a"
                    href={`/testsuites/${testSuite.testsuiteid}`}
                    variant="text"
                    size="small"
                    sx={{
                      textDecoration: 'underline',
                      color: 'text.primary',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'text.secondary',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Edit test suite
                  </Button>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                overflow: 'hidden',
                backgroundColor: '#F9F9F9',
                p: 2,
              }}
            >
              {loadingDetails ? (
                <Box>
                  {[...Array(6)].map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={24}
                        sx={{ mb: 1 }}
                      />
                      <Box>
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={20}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width="70%"
                          height={20}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton variant="text" width="75%" height={20} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : suiteDetails && suiteDetails.payload ? (
                <Box>
                  {suiteDetails.payload.category.map(
                    (category, categoryIndex) => (
                      <Box key={categoryIndex}>
                        {category.subcategory &&
                          category.subcategory.map(
                            (subcategory, subcategoryIndex) => {
                              const categoryName = category.categoryname
                                ? `${category.categoryname} > ${subcategory.subcategoryname}`
                                : `${subcategory.subcategoryid} > ${subcategory.subcategoryname}`;

                              const isLastSubcategory =
                                categoryIndex ===
                                  suiteDetails.payload.category.length - 1 &&
                                subcategoryIndex ===
                                  category.subcategory.length - 1;

                              return (
                                <Box key={subcategoryIndex}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{
                                      color: 'text.primary',
                                    }}
                                  >
                                    {categoryName}
                                  </Typography>
                                  <List
                                    sx={{
                                      paddingTop: '0px !important',
                                      paddingBottom: '0px !important',
                                      marginBottom: '8px !important',
                                    }}
                                  >
                                    {subcategory.testcases.map(
                                      (testcase, testcaseIndex) => (
                                        <ListItem
                                          key={testcaseIndex}
                                          sx={{
                                            paddingTop: '4px !important',
                                            paddingBottom: '4px !important',
                                            minHeight: 'auto',
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: 'text.primary',
                                                  fontSize: '14px',
                                                }}
                                              >
                                                {testcase.name}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                  {!isLastSubcategory && (
                                    <Divider
                                      sx={{
                                        borderColor: 'divider',
                                        mt: '8px',
                                        mb: '16px',
                                      }}
                                    />
                                  )}
                                </Box>
                              );
                            }
                          )}
                      </Box>
                    )
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No test cases found.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Shared with Section */}
          <Accordion position="right" sx={accordionStyles}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="shared-content"
              id="shared-header"
              sx={accordionSummaryStyles}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mr: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold !important', fontSize: '18px' }}
                >
                  Shared with
                </Typography>
                {testSuite?.userlist?.users?.length !== 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleShare}
                    sx={{
                      textDecoration: 'underline',
                      color: 'text.primary',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'text.secondary',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Share
                  </Button>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                pr: !testSuite?.userlist?.users?.length
                  ? '12px !important'
                  : 'inherit',
              }}
            >
              {!testSuite?.userlist?.users?.length ? (
                // Not shared - show info box
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '48px',
                    border: '1px solid #5F60FF',
                    backgroundColor: 'rgba(95, 96, 255, 0.1)',
                    borderRadius: 1,
                    px: 2,
                    mb: '16px',
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: '#5F60FF',
                      fontSize: '24px',
                      mr: 2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      flex: 1,
                    }}
                  >
                    You have not shared this test suite with anyone
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleShare}
                    sx={{
                      textDecoration: 'underline',
                      color: 'text.primary',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'text.secondary',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Share
                  </Button>
                </Box>
              ) : (
                // Shared - show user list
                <Box>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ fontWeight: 'bold !important', fontSize: '14px' }}
                      >
                        Name
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ fontWeight: 'bold !important', fontSize: '14px' }}
                      >
                        Email
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ fontWeight: 'bold !important', fontSize: '14px' }}
                      >
                        Role
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mb: 1 }} />
                  {testSuite.userlist.users.map((user, index) => {
                    const { firstName, lastName } = extractNameFromEmail(
                      user.user
                    );
                    const isLastUser =
                      index === testSuite.userlist.users.length - 1;
                    return (
                      <Box key={index}>
                        <Grid
                          container
                          spacing={2}
                          sx={{ mb: 1.5, height: '42px', alignItems: 'center' }}
                        >
                          <Grid item xs={4} sx={{ py: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '14px' }}
                            >
                              {firstName} {lastName}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ py: 0 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '14px' }}
                            >
                              {user.user}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ py: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '14px' }}
                            >
                              {user.role || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                        {!isLastUser && <Divider sx={{ mb: '8px' }} />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      {/* Schedule Modal */}
      {openScheduleModal && testSuite && (
        <ScheduleModal
          open={openScheduleModal}
          onClose={() => setOpenScheduleModal(false)}
          testSuiteId={testSuite.testsuiteid}
          schedule={{
            disabled: testSuite.isdisabled,
            cronExpression: testSuite.scheduledtime,
            dateStart: testSuite.start_date,
            dateEnd: testSuite.end_date,
            updateXray: testSuite.updateXray,
            jiraProjectId: testSuite.jiraProjectId,
            jiraTestExecutionId: testSuite.jiraTestExecutionId,
          }}
        />
      )}

      {/* Delete Modal */}
      {openDeleteModal && testSuite && (
        <ConfirmationModal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Remove test suite"
          message="Proceeding will remove the selected test suite. This action cannot be undone."
          submitButtonText="Remove test suite"
          loading={isDeleting}
        />
      )}
    </Drawer>
  );
};

export default TestSuiteDetailsDrawer;
